import React, { useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { DoctorDto } from "../../../web-api-client";
import { DoctorSearchAddTable } from "./DoctorSearchAddTable";
import { AddDoctorPayload } from "./WeekOverview";


type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;

  timeRange: string;

  doctorSuggestions: DoctorDto[];
  isDoctorLoading?: boolean;
  onDoctorSearchChange?: (query: string) => void;

  defaultQuota?: number;

  initialSelectedDoctors?: DoctorDto[];
  initialQuotaMap?: Record<string, number>;

  onConfirm: (payload: AddDoctorPayload) => void;
  isSubmitting?: boolean;
};

export function AddDoctorsToTimeRangeDialog({
  open,
  onOpenChange,
  timeRange,
  doctorSuggestions,
  isDoctorLoading,
  onDoctorSearchChange,
  defaultQuota = 10,
  initialSelectedDoctors,
  initialQuotaMap,
  onConfirm,
  isSubmitting,
}: Props) {
  const [selectedDoctors, setSelectedDoctors] = useState<DoctorDto[]>(
    initialSelectedDoctors ?? []
  );
  const [quotaByDoctorId, setQuotaByDoctorId] = useState<Record<string, number>>(
    initialQuotaMap ?? {}
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedDoctors(initialSelectedDoctors ?? []);
    setQuotaByDoctorId(initialQuotaMap ?? {});
  }, [open, initialSelectedDoctors, initialQuotaMap]);

  const selectedCount = selectedDoctors.length;

  const canConfirm = useMemo(() => {
    return selectedCount > 0 && !isSubmitting;
  }, [selectedCount, isSubmitting]);

  const handleConfirm = () => {
    const payload: AddDoctorPayload = selectedDoctors
      .filter((d) => !!d.doctorId)
      .map((d) => ({
        doctorId: d.doctorId!,
        quota: quotaByDoctorId[d.doctorId!] ?? defaultQuota,
      }));

    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[560px] p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-semibold">
            Thêm bác sĩ vào khung giờ
          </DialogTitle>

          <div className="mt-2 flex items-center gap-2 text-base font-bold text-green-800">
            <img src="/icons/clock.png" className="w-5 h-5" alt="Clock" />
            <span>{timeRange}</span>
          </div>
        </DialogHeader>

        <div className="px-6 space-y-4 min-h-80">
          <DoctorSearchAddTable
            suggestions={doctorSuggestions}
            isLoading={isDoctorLoading}
            onSearchChange={onDoctorSearchChange}
            defaultQuota={defaultQuota}
            selectedDoctors={selectedDoctors}
            onChangeSelected={setSelectedDoctors}
            quotaByDoctorId={quotaByDoctorId}
            onChangeQuotaMap={setQuotaByDoctorId}
            placeholder="Tìm bác sĩ..."
          />

          <div className="text-xs text-muted-foreground">
            Đã chọn:{" "}
            <span className="font-semibold text-foreground">
              {selectedCount}
            </span>{" "}
            bác sĩ
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Hủy
          </Button>

          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-green-600 hover:bg-green-700"
          >
            Thêm bác sĩ đã chọn
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}